import React from 'react';
import { ShieldCheck, Lock, Eye, FileText, CheckCircle2 } from 'lucide-react';
import { Card, Badge } from '../ui';
import { companyInfo } from '../../data/company';

export const PrivacyPolicySection: React.FC = () => {
  return (
    <section className="py-24 relative min-h-screen bg-white dark:bg-[#080e1a]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-3">
          <Badge variant="success" className="px-3.5 py-1 text-xs uppercase tracking-wider">
            <ShieldCheck size={14} /> Compliance & Data Protection
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#184166] dark:text-white tracking-tight">
            Privacy Policy & Data Security Statement
          </h1>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Last Updated: August 2026 • Formatted in compliance with Republic Act No. 10173 (Philippine Data Privacy Act of 2012)
          </p>
        </div>

        <Card variant="glass" className="p-8 sm:p-10 space-y-8 text-slate-700 dark:text-slate-300 text-sm leading-relaxed bg-[#f5f9fc] dark:bg-[#0f1b2d] border border-slate-200 dark:border-slate-800">
          {/* Section 1 */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-[#184166] dark:text-white flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <FileText size={18} className="text-[#4a82c2]" /> 1. Commitment to Data Privacy
            </h3>
            <p>
              Technook Global (&quot;Technook&quot;, &quot;we&quot;, &quot;our&quot;, or &quot;us&quot;), headquartered at Imperial Plaza, Diosdado Macapagal Blvd., Tambo, Parañaque City, Metro Manila, Philippines, respects your personal privacy and is committed to protecting your personal information in compliance with Republic Act No. 10173 (Data Privacy Act of 2012) and international standards.
            </p>
          </div>

          {/* Section 2 */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-[#184166] dark:text-white flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <Eye size={18} className="text-[#08adae]" /> 2. Information We Collect
            </h3>
            <p>When you submit an RFP inquiry, schedule an appointment, or interact with our digital platform, we may collect:</p>
            <ul className="space-y-1.5 pl-4 text-xs text-slate-600 dark:text-slate-400">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4a82c2]" /> Contact Details: Full Name, Business Email Address, Phone/Viber Number, Company Name.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4a82c2]" /> Technical Data: IP address, browser type, device information, and site interaction logs.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-[#4a82c2]" /> Project Specifications: RFP descriptions, budget estimates, and scheduled consultation notes.</li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-[#184166] dark:text-white flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <Lock size={18} className="text-emerald-500" /> 3. Purpose & Data Use
            </h3>
            <p>Your personal data is strictly used for legitimate business purposes including:</p>
            <ul className="space-y-1.5 pl-4 text-xs text-slate-600 dark:text-slate-400">
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500" /> Preparing custom RFP technical proposals and scoping estimations.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500" /> Scheduling 1-on-1 strategy sessions with Technook software architects.</li>
              <li className="flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500" /> Maintaining enterprise service delivery, support, and SLA reporting.</li>
            </ul>
          </div>

          {/* Section 4 */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-[#184166] dark:text-white flex items-center gap-2 border-b border-slate-200 dark:border-slate-800 pb-2">
              <ShieldCheck size={18} className="text-amber-500" /> 4. Security & User Rights
            </h3>
            <p>
              We implement industry-standard AES encryption, access controls, and secure socket layers (SSL) to protect all submitted data against unauthorized access. Under RA 10173, you retain rights to request access, correction, or deletion of your personal records.
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 pt-2">
              For privacy inquiries, contact our Data Protection Office at <span className="text-[#4a82c2] font-mono">{companyInfo.email}</span>.
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
};
